import React from "react"
import { Link } from "gatsby"

import TopicList from "../topic/topiclist"
import PlatformCard from "../platform/platformcard"
import FeatureList from "../feature/featurelist"

const HowItWorks = ({
  isMobile,
  topicNodes,
  topicTotalCount,
  platformNodes,
  platformTotalCount,
  featureNodes,
}) => {
  const platformsToDisplay = platformNodes
    .slice()
    .sort((a, b) => (a.promoted < b.promoted ? 1 : b.promoted < a.promoted ? -1 : 0))
    .filter((p) => p.slug !== "independent")
    .slice(0, 9)

  const independent = platformNodes.filter((p) => p.slug === "independent")[0]
  const promotedPlatform = platformsToDisplay.filter((p) => p.promoted)[0]

  return (
    <section className="reveal" aria-labelledby="how-it-works-heading">
      <div className="mt-2 py-16 bg-gray-50 overflow-hidden lg:py-24 rounded-xl sm:rounded-2xl">
        <div className="relative max-w-xl mx-auto px-4 sm:px-6 lg:px-8 lg:max-w-7xl">
          <div className="relative text-center max-w-3xl mx-auto">
            <p className="section-eyebrow">How it works</p>
            <h2
              id="how-it-works-heading"
              className="mt-3 text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl"
            >
              Find your perfect community
            </h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Start by topic, pick your platform, or filter by what you want to
              do there. Every listing is structured consistently so you can
              compare communities at a glance.
            </p>
          </div>

          {/* 01 / Browse by topic */}
          <div className="relative mt-14 lg:mt-20 lg:grid lg:grid-cols-3 lg:gap-12">
            <div className="relative">
              <p className="section-eyebrow text-gray-500">01 / Browse by topic</p>
              <h3 className="mt-3 text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                Every niche has a home
              </h3>
              <p className="mt-3 text-lg text-gray-600">
                From startup founders and indie hackers to writers, designers,
                gamers, and more, our {topicTotalCount} topic pages cover the
                full spectrum of what people care about.
              </p>
              <p className="mt-3 text-lg text-gray-600">
                Each topic page lists every relevant community with member
                counts, platform, features, and direct links.
              </p>
              <p className="mt-3 text-lg text-gray-600">
                Missing a topic?{" "}
                <Link
                  to="/suggest/"
                  className="text-rose-600 font-semibold hover:underline"
                >
                  Suggest it
                </Link>{" "}
                and we will curate it.
              </p>
            </div>

            <div className="mt-10 -mx-4 relative lg:mt-0 col-span-2 bg-white rounded-xl border border-gray-100 border-l-4 border-l-rose-500 shadow-sm overflow-hidden p-4 sm:p-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex items-center">
                  <h4 className="pr-3 bg-white text-lg font-semibold text-gray-900">
                    Popular topics
                  </h4>
                </div>
              </div>
              <div className="mt-4 text-gray-600">
                <TopicList
                  topics={topicNodes.slice(0, isMobile ? 6 : 9)}
                  mini={true}
                  cols={3}
                />
                <div className="p-6 pb-0 mx-auto text-center">
                  <Link
                    to="/topics/"
                    className="inline-block text-sm font-semibold text-rose-600 hover:text-rose-700 hover:underline"
                  >
                    Browse all {topicTotalCount} topics in the directory
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* 02 / Choose your platform */}
          <div className="relative mt-16 sm:mt-20 lg:mt-24">
            <div className="lg:grid lg:grid-flow-row-dense lg:grid-cols-3 lg:gap-12">
              <div className="lg:col-start-3">
                <p className="section-eyebrow text-gray-500">
                  02 / Choose your platform
                </p>
                <h3 className="mt-3 text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                  Discord, Reddit, Slack, and {platformTotalCount - 3} more
                </h3>
                <p className="mt-3 text-lg text-gray-600">
                  Different platforms suit different styles. Chat-first, async,
                  video-heavy, forum-based, or token-gated, we catalogue
                  communities across the entire ecosystem.
                </p>
                {independent ? (
                  <p className="mt-3 text-lg text-gray-600">
                    Prefer something entirely custom? Explore{" "}
                    <Link
                      to="/platforms/independent/"
                      className="text-rose-600 font-semibold hover:underline"
                    >
                      {independent.count} independent communities
                    </Link>{" "}
                    that run their own software.
                  </p>
                ) : null}
                {promotedPlatform ? (
                  <p className="mt-3 text-lg text-gray-600">
                    Building your own? We recommend{" "}
                    <Link
                      to={`/platforms/${promotedPlatform.slug}/`}
                      className="text-rose-600 font-semibold hover:underline"
                    >
                      {promotedPlatform.name}
                    </Link>
                    .
                  </p>
                ) : null}
              </div>

              <div className="mt-10 -mx-4 relative lg:mt-0 lg:col-start-1 col-span-2 bg-white rounded-xl border border-gray-100 border-l-4 border-l-rose-500 shadow-sm overflow-hidden p-6 sm:p-8">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex items-center">
                    <h4 className="pr-3 bg-white text-lg font-semibold text-gray-900">
                      Platforms in the directory
                    </h4>
                  </div>
                </div>
                <div className="mt-4 text-gray-600">
                  <ul className="grid grid-cols-3 gap-x-3 gap-y-5 sm:gap-x-4">
                    {platformsToDisplay
                      .filter((n, i) => (!isMobile ? true : i <= 5))
                      .map((p) => (
                        <PlatformCard
                          key={p.slug}
                          platform={p}
                          highlightPromoted={true}
                          // compact={true}
                        />
                      ))}
                  </ul>
                  <div className="p-6 pb-0 mx-auto text-center">
                    <Link
                      to="/platforms/"
                      className="inline-block text-sm font-semibold text-rose-600 hover:text-rose-700 hover:underline"
                    >
                      Browse all {platformTotalCount} platforms
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 03 / Filter by feature */}
          <div className="relative mt-16 lg:mt-24 lg:grid lg:grid-cols-3 lg:gap-12">
            <div className="relative">
              <p className="section-eyebrow text-gray-500">03 / Filter by feature</p>
              <h3 className="mt-3 text-2xl font-extrabold text-gray-900 tracking-tight sm:text-3xl">
                Match communities to what you actually want
              </h3>
              <p className="mt-3 text-lg text-gray-600">
                Not all communities are equal. Some have active jobs boards,
                live events, mentorship programs, or exclusive member perks.
                Others require an application or have a paid membership.
              </p>
              <p className="mt-3 text-lg text-gray-600">
                Every listing in Hive Index is tagged with these features so
                you can filter to exactly the kind of community you are looking
                for, without wading through ones that are not a fit.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                <Link
                  to="/features/"
                  className="text-rose-600 font-semibold hover:underline"
                >
                  Browse by feature tag
                </Link>
              </p>
            </div>

            <div className="mt-10 -mx-4 relative lg:mt-0 col-span-2 bg-white rounded-xl border border-gray-100 border-l-4 border-l-rose-500 shadow-sm overflow-hidden p-6 sm:p-8">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex items-center">
                  <h4 className="pr-3 bg-white text-lg font-semibold text-gray-900">
                    Community features we track
                  </h4>
                </div>
              </div>
              <div className="mt-4">
                <FeatureList features={featureNodes} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HowItWorks
